import { useState } from 'react';
import { Box, Button, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSolicitarCodigoMutation, useValidarCodigoMutation, useCompletarCadastroMutation } from '../api';
import type { SessaoCliente } from '../api';
import { definirTokens } from '../store/auth-slice';
import { rastrearCadastroConcluido } from '../lib/tracking';
import { LgpdAviso } from './lgpd-aviso';

type Etapa = 'email' | 'codigo' | 'cadastro';

/**
 * Formulário de login/cadastro do cliente (e-mail → código → cadastro), sem
 * "casca": não tem container, título nem botão de fechar próprios — quem usa
 * decide a moldura (hoje o DialogLogin, que põe o título no cabeçalho).
 *
 * Ao concluir, grava os tokens na store e chama `onSucesso`. Os tokens já estão
 * na store antes de `onSucesso`, então queries gateadas por token (getMe,
 * getEnderecos, meus-pedidos) reexecutam com o token novo — quem chama só
 * precisa fechar o modal e segue exatamente de onde estava.
 */
export function LoginConteudo({ onSucesso }: { onSucesso: () => void }): JSX.Element {
  const [etapa, setEtapa] = useState<Etapa>('email');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cpf, setCpf] = useState('');
  const [cadastroToken, setCadastroToken] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [codigoReenviado, setCodigoReenviado] = useState(false);
  // Validade real vinda do backend; 15 é só o fallback antes da primeira resposta.
  const [minutosValidade, setMinutosValidade] = useState(15);

  const dispatch = useDispatch();
  const [solicitar, statusSolicitar] = useSolicitarCodigoMutation();
  const [validar, statusValidar] = useValidarCodigoMutation();
  const [completarCadastro, statusCadastro] = useCompletarCadastroMutation();

  function concluirLogin(sessao: SessaoCliente): void {
    dispatch(definirTokens({ accessToken: sessao.accessToken, refreshToken: sessao.refreshToken }));
    onSucesso();
  }

  async function enviarEmail(): Promise<void> {
    setErro(null);
    setCodigoReenviado(false);
    try {
      // Resposta sem corpo não pode travar o login: cai no fallback e segue.
      const resposta = await solicitar({ email }).unwrap();
      setMinutosValidade(resposta?.expira_em_min ?? 15);
      setEtapa('codigo');
    } catch (err) {
      setErro(extrairMensagem(err) ?? 'Falha ao solicitar código');
    }
  }

  async function reenviarCodigo(): Promise<void> {
    setErro(null);
    setCodigoReenviado(false);
    try {
      const resposta = await solicitar({ email }).unwrap();
      setMinutosValidade(resposta?.expira_em_min ?? 15);
      setCodigoReenviado(true);
      setCodigo('');
    } catch (err) {
      setErro(extrairMensagem(err) ?? 'Falha ao reenviar o código');
    }
  }

  async function confirmarCodigo(): Promise<void> {
    setErro(null);
    try {
      const resultado = await validar({ email, codigo }).unwrap();
      if ('precisaCadastro' in resultado) {
        // E-mail sem cadastro: só agora pedimos nome/CPF.
        setCadastroToken(resultado.cadastroToken);
        setEtapa('cadastro');
        return;
      }
      concluirLogin(resultado);
    } catch (err) {
      setErro(extrairMensagem(err) ?? 'Código inválido');
    }
  }

  async function finalizarCadastro(): Promise<void> {
    setErro(null);
    try {
      const sessao = await completarCadastro({ cadastroToken, nome, telefone, cpf: cpf || undefined }).unwrap();
      rastrearCadastroConcluido();
      concluirLogin(sessao);
    } catch (err) {
      setErro(extrairMensagem(err) ?? 'Não foi possível concluir o cadastro');
    }
  }

  const carregando = statusSolicitar.isLoading || statusValidar.isLoading || statusCadastro.isLoading;
  const caiNoSpam = ehDominioQueCaiNoSpam(email);

  return (
    <>
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {etapa === 'email' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="E-mail"
            type="email"
            value={email}
            // E-mail não tem espaços — remove qualquer um já na digitação.
            onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && emailValido(email) && !carregando) void enviarEmail();
            }}
            error={!!email && !emailValido(email)}
            helperText={!!email && !emailValido(email) ? 'Informe um e-mail válido' : undefined}
            autoFocus
            autoComplete="email"
            inputProps={{ inputMode: 'email', enterKeyHint: 'go' }}
          />
          <LgpdAviso />
          <Button variant="contained" onClick={enviarEmail} disabled={!emailValido(email) || carregando}>
            {carregando ? <CircularProgress size={20} /> : 'Enviar código'}
          </Button>
        </Box>
      )}

      {etapa === 'codigo' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            Enviamos um código de 6 dígitos para{' '}
            {/* break-word, não break-all: o e-mail desce inteiro se couber na linha. */}
            <Box component="span" sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
              {email}
            </Box>
            . Ele vale por {minutosValidade} minutos.
          </Typography>
          {/* Só a abertura muda: no Hotmail o motivo vem antes de onde procurar. */}
          <Alert severity={caiNoSpam ? 'warning' : 'info'}>
            <strong>
              {caiNoSpam
                ? 'No Hotmail e no Outlook o código quase sempre cai no spam.'
                : 'Não achou o e-mail?'}
            </strong>{' '}
            {caiNoSpam ? 'Procure lá por' : 'Veja o spam (lixo eletrônico) e procure por'}{' '}
            <strong>Sischef</strong> — o código já aparece no título, sem precisar abrir.
          </Alert>
          {codigoReenviado && (
            <Alert severity="success" onClose={() => setCodigoReenviado(false)}>
              Um novo código foi enviado para {email}. Use o código do e-mail mais novo; o anterior não vale mais.
            </Alert>
          )}
          <TextField
            label="Código"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            autoComplete="one-time-code"
            inputProps={{ maxLength: 6, inputMode: 'numeric', enterKeyHint: 'done' }}
          />
          <Button variant="contained" onClick={confirmarCodigo} disabled={codigo.length !== 6 || carregando}>
            {carregando ? <CircularProgress size={20} /> : 'Entrar'}
          </Button>
          <Button onClick={reenviarCodigo} disabled={carregando}>
            {statusSolicitar.isLoading ? <CircularProgress size={20} /> : 'Reenviar código'}
          </Button>
          <Button onClick={() => setEtapa('email')} disabled={carregando}>
            Trocar e-mail
          </Button>
        </Box>
      )}

      {etapa === 'cadastro' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">É seu primeiro acesso. Complete o cadastro para finalizar o pedido.</Typography>
          <TextField
            label="Seu nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            autoFocus
            autoComplete="name"
          />
          <TextField
            label="Telefone (WhatsApp)"
            type="tel"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            autoComplete="tel"
            inputProps={{ inputMode: 'tel' }}
            error={!!telefone && !telefoneValido(telefone)}
            helperText={!!telefone && !telefoneValido(telefone) ? 'Informe um telefone com DDD' : undefined}
          />
          <TextField
            label="CPF (opcional)"
            value={cpf}
            onChange={(e) => setCpf(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
          />
          <LgpdAviso />
          <Button
            variant="contained"
            onClick={finalizarCadastro}
            disabled={!nome.trim() || !telefoneValido(telefone) || carregando}
          >
            {carregando ? <CircularProgress size={20} /> : 'Concluir cadastro'}
          </Button>
        </Box>
      )}
    </>
  );
}

/** Validação simples de formato de e-mail (o backend revalida com zod .email()). */
function emailValido(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Domínios cujo filtro manda o código para o spam quase sempre — relato do
 * suporte com Hotmail, e as quatro marcas caem na mesma caixa da Microsoft.
 * Só troca o tom do aviso; falso positivo custa um alerta laranja a mais.
 */
export function ehDominioQueCaiNoSpam(email: string): boolean {
  const dominio = email.trim().toLowerCase().split('@')[1] ?? '';
  return /^(hotmail|outlook|live|msn)\./.test(dominio);
}

/** Telefone obrigatório no cadastro: ao menos 10 dígitos (DDD + número). */
function telefoneValido(telefone: string): boolean {
  return telefone.replace(/\D/g, '').length >= 10;
}

function extrairMensagem(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const dados = (err as { data?: { mensagem?: string } }).data;
    return dados?.mensagem ?? null;
  }
  return null;
}
